import { memo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import useAxiosPrivate from 'hooks/useAxiosPrivate';

// action -> o'qiladigan yorliq
const ACTION_LABELS = {
  ADDHOME: 'Uy biriktirildi',
  REMOVEHOME: 'Uy olib tashlandi',
  name: 'Shartnoma nomi',
  sum: 'Shartnoma summasi',
  start_price: "Boshlang'ich to'lov",
  discount: 'Chegirma',
  user_id: "Mas'ul xodim",
  operator_id: 'Operator (vositachi)',
  client_id: 'Mijoz',
  home_id: 'Asosiy uy',
  date: 'Sana',
  comment: 'Izoh',
  isrepaired: "Ta'mir holati",
  type_id: 'Shartnoma turi',
  square: 'Maydon',
  total_square: 'Jami maydon',
  price: 'Narx (1m²)',
  status: 'Holat',
  isvalute: 'Valyuta',
  is_notarial: 'Notarial',
  peniya: 'Peniya',
  cancel_by_id: 'Bekor qilgan xodim',
  cancel_date: 'Bekor qilingan sana',
  cancel_text: 'Bekor qilish sababi',
};

// "499200000 → 500000000" ichidagi sof sonlarni minglik ajratkich bilan chiqaradi;
// sana (2026-07-17) va matnlar tegilmaydi
const formatNumberPart = (part) => {
  const trimmed = part.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) return part;
  const [int, dec] = trimmed.split('.');
  const formatted = int.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return dec && !/^0+$/.test(dec) ? `${formatted}.${dec}` : formatted;
};

const formatValue = (value) =>
  value
    .split(' → ')
    .map(formatNumberPart)
    .join(' → ');

/**
 * Shartnoma sahifasining eng pastidagi "Shartnoma tarixi" bo'limi —
 * barcha o'zgarishlar (uy, summa, xodim, nom...). Tarix bo'lmasa ko'rinmaydi.
 */
const ContractHistorySection = ({ contractId }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const [page, setPage] = useState(1);

  const { data, isFetching } = useQuery({
    queryKey: ['contractHistories', contractId, page],
    queryFn: async () =>
      (
        await axiosPrivate.get(
          `/admin/contract/histories?contract_id=${contractId}&page=${page}`
        )
      ).data?.data,
    enabled: !!contractId,
    keepPreviousData: true,
    retry: false,
  });

  const rows = data?.data || [];
  const total = data?.total || 0;
  const pageCount = Math.ceil(total / (data?.per_page || 20)) || 1;

  if (!total && !isFetching) return null;

  return (
    <div className='mt-4'>
      <Accordion
        defaultExpanded
        disableGutters
        className='!rounded-xl !border !border-gray-200 !shadow-none before:!hidden'
      >
        <AccordionSummary
          expandIcon={<i className='bi bi-chevron-down' />}
          className='!bg-gray-100 !rounded-t-xl'
        >
          <span className='font-medium'>
            <i className='bi bi-clock-history mr-2' />
            {t('contract.view.history.title')} ({total})
          </span>
        </AccordionSummary>
        <AccordionDetails className='!p-4'>
          {isFetching && rows.length === 0 ? (
            <div className='flex justify-center py-6'>
              <CircularProgress size={26} />
            </div>
          ) : (
            <TableContainer className='!shadow-none !border !border-gray-200 !rounded-xl'>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    {[
                      t('common.fields.date'),
                      t('common.fields.staff'),
                      t('contract.view.history.action'),
                      t('contract.view.history.value'),
                    ].map((label, i) => (
                      <TableCell
                        key={`h-${i}`}
                        align='left'
                        sx={{ backgroundColor: 'rgb(var(--c-gray-50))', fontSize: '13px' }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row?.id} hover tabIndex={-1}>
                      <TableCell className='whitespace-nowrap'>
                        {row?.created_at
                          ? moment(row.created_at).format('DD.MM.YYYY HH:mm')
                          : '—'}
                      </TableCell>
                      <TableCell>{row?.user?.name || '—'}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center rounded-md text-[12px] leading-none font-medium px-2.5 py-1 whitespace-nowrap ring-1 ring-inset ring-black/5 ${
                            row?.action === 'ADDHOME'
                              ? 'bg-green-50 text-green-700'
                              : row?.action === 'REMOVEHOME'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}
                        >
                          {ACTION_LABELS[row?.action] || row?.action}
                        </span>
                      </TableCell>
                      <TableCell>
                        {row?.value ? formatValue(row.value) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pageCount > 1 && (
            <div className='flex justify-center mt-3'>
              <Pagination
                count={pageCount}
                page={page}
                onChange={(e, v) => setPage(v)}
                shape='rounded'
                color='primary'
                disabled={isFetching}
              />
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default memo(ContractHistorySection);
